import { useEffect, useRef } from 'react';
import { Dialog, Stack } from '@mui/material';
import { color } from '@/colors';
import { AppXBtn } from '@/UI/AppXBtn/AppXBtn';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { FigmaPromoHeader } from './FigmaPromoHeader';
import { FigmaPromoWorkflowCards } from './FigmaPromoWorkflowCards';
import { FigmaPromoFooter } from './FigmaPromoFooter';
import type { SxProps, Theme } from '@mui/material/styles';
import type { WelcomeDialogProps } from '@/components/WelcomeModal/types';

const DARK_SCROLLBAR_SX: SxProps<Theme> = {
  '&::-webkit-scrollbar': { width: '6px' },
  '&::-webkit-scrollbar-track': { backgroundColor: color.Black88 },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: color.Black40,
    '&:hover': { backgroundColor: color.Black64 },
  },
};

const SOURCE = 'figma_promo_welcome';

export function FigmaPromoWelcomeDialog({ isOpen, close }: WelcomeDialogProps) {
  const { track } = useAnalytics();
  const hasTrackedShown = useRef(false);

  useEffect(() => {
    if (isOpen && !hasTrackedShown.current) {
      hasTrackedShown.current = true;
      track('figma_promo_welcome_shown', { source: SOURCE }, TrackTypeEnum.BI);
    }
  }, [isOpen, track]);

  const handleDismiss = (method: 'x_button' | 'escape') => {
    track('figma_promo_welcome_dismissed', { source: SOURCE, method }, TrackTypeEnum.BI);
    close();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        handleDismiss('escape');
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: color.Black92,
          backgroundImage: 'none',
          borderRadius: 4,
          overflow: 'auto',
          position: 'relative',
          maxWidth: 1020,
          ...DARK_SCROLLBAR_SX,
        },
      }}
    >
      <AppXBtn
        size={20}
        onClick={() => handleDismiss('x_button')}
        mode="on-dark"
        sx={{ position: 'absolute', top: 16, right: 16, zIndex: 2 }}
      />

      <Stack alignItems="center" sx={{ position: 'relative', zIndex: 1, p: 8, gap: '42px' }}>
        <FigmaPromoHeader />
        <FigmaPromoWorkflowCards />
        <FigmaPromoFooter />
      </Stack>
    </Dialog>
  );
}
